"use client";

import { getMedia } from "@trydig/storyblok";
import { clsx } from "@trydig/ui";
import Image from "next/image";
import type { ComponentProps } from "react";
import { Lqip } from "@/components/ui/Lqip";
import { VideoPlayer } from "@/components/ui/sections/VideoPlayer";
import type { AssetStoryblok } from "@/generated/sb/sb-types";
import { parseFocusPoint } from "@/lib/utils/media";

export interface AssetProps {
	asset?: AssetStoryblok;
	className?: string;
	videoProps?: ComponentProps<"video">;
	sizes?: string;
	style?: React.CSSProperties;
	priority?: boolean;
	/**
	 * Show a Storyblok-generated blur placeholder behind the image while it
	 * loads. Defaults to `true` — pass `false` to skip the blur layer (useful
	 * for transparent PNGs, or above-the-fold heroes where you'd rather paint
	 * straight from the page background to the real image).
	 */
	lqip?: boolean;
	/**
	 * Opt a video asset in to audio. Forwarded to `VideoPlayer`, which needs a
	 * surrounding `VideoAudioProvider` plus a `VideoAudioToggle` to be useful.
	 * Ignored for images. A first-class prop rather than part of `videoProps`
	 * because it isn't a `<video>` attribute.
	 */
	sound?: boolean;
}

/**
 * Build a low-quality blur placeholder URL via Storyblok's image service.
 * Returns `undefined` for non-Storyblok assets. The image-service pipeline
 * works on any `*.storyblok.com` subdomain — `a.storyblok.com` for vanilla
 * assets, `img2.storyblok.com` for some account/CDN configurations — and on
 * any path that contains `/f/` (the Storyblok asset-folder marker).
 *
 * Reference: https://www.storyblok.com/docs/api/image-service
 */
const storyblokLqipUrl = (url: string): string | undefined => {
	const isStoryblok = url.includes("storyblok.com") || url.includes("/f/");
	if (!isStoryblok) return undefined;
	return `${url}/m/32x0/filters:blur(0)`;
};

export const Asset = ({
	asset,
	className,
	sizes,
	videoProps,
	style,
	priority,
	lqip = true,
	sound = false,
}: AssetProps) => {
	if (!asset) return null;

	const media = getMedia(asset);

	const focusPoint = parseFocusPoint(asset?.focus as string | undefined, {
		width: media?.width,
		height: media?.height,
	});

	if (!media) return null;

	const lqipUrl = lqip && media.type === "image" ? storyblokLqipUrl(media.url) : undefined;

	const focusBackgroundPosition = focusPoint
		? `${focusPoint.xPercent}% ${focusPoint.yPercent}%`
		: undefined;

	return (
		<div className={clsx("relative h-full w-full overflow-hidden", className)} style={style}>
			{media.type === "video" ? (
				<VideoPlayer src={media.url} sound={sound} {...videoProps} />
			) : (
				<>
					{lqipUrl && <Lqip src={lqipUrl} objectPosition={focusBackgroundPosition} />}
					<Image
						className="h-full w-full object-cover"
						style={{ objectPosition: focusBackgroundPosition } as React.CSSProperties}
						fill
						priority={priority}
						src={media.url}
						alt={media.alt || ""}
						sizes={sizes}
					/>
				</>
			)}
		</div>
	);
};
